import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Drawer,
  Link,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { ReactComponent as AddIcon } from "../../asserts/logo/add.svg";
import { ReactComponent as FolderEmptyIcon } from "../../asserts/logo/Folder_empty.svg";
import { uniqueItemId } from "./frameworkUtils";
import {
  normalizeThresholdPercent,
  isGate0MappedQuestion,
  gate0FieldLabel,
  shouldKeepGate0Mapping,
} from "./sectionEditorUtils";
import { sectionRequiredFromItems } from "../../utils/chatUtils";
import ConfirmFieldUpdateDialog from "./ConfirmFieldUpdateDialog";

const CONFIRM_NAME_COPY = {
  title: "Confirm Section Name Update",
  description:
    "Updating the section name may affect assessment results and reporting.\nDo you want to continue?",
};

const CONFIRM_SCORE_COPY = {
  title: "Confirm Confidence Score Update",
  description:
    "Updating the confidence score may affect assessment results and reporting.\nDo you want to continue?",
};

const C = {
  primary: "#005BF0",
  border: "#E6E6E6",
  text: "#383838",
  muted: "#707070",
  required: "#D6441F",
  drawerWidth: 480,
};

function RequiredMark() {
  return (
    <Box component="span" sx={{ color: C.required, ml: 0.25 }}>
      *
    </Box>
  );
}

const questionLabelSx = {
  fontFamily: "Lato, sans-serif",
  fontWeight: 700,
  fontSize: 16,
  lineHeight: "24px",
  letterSpacing: 0,
  color: "#707070",
};

function QuestionLabel({ number, required }) {
  return (
    <Typography component="span" sx={questionLabelSx}>
      {required !== false && (
        <Box component="span" sx={{ color: C.required, mr: 0.25 }}>
          *
        </Box>
      )}
      Question-{number}
    </Typography>
  );
}

const questionsHeadingSx = {
  fontFamily: "Lato, sans-serif",
  fontWeight: 700,
  fontSize: 16,
  lineHeight: "24px",
  letterSpacing: 0,
  color: "#555555",
};

const DRAWER_BTN_SHADOW = "0px 1px 2px 0px #0000000A";

const drawerFooterSx = {
  flexShrink: 0,
  width: "100%",
  minHeight: 88,
  boxSizing: "border-box",
  p: "24px",
  gap: "16px",
  bgcolor: "#F5F5F5",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
};

const drawerFooterNoteSx = {
  fontFamily: "Lato, sans-serif",
  fontWeight: 400,
  fontSize: 13,
  lineHeight: "24px",
  letterSpacing: 0,
  color: "#555555",
  flex: 1,
  minWidth: 0,
};

const drawerCancelBtnSx = {
  textTransform: "none",
  fontFamily: "Lato, sans-serif",
  fontWeight: 700,
  fontSize: 15,
  lineHeight: "24px",
  letterSpacing: 0,
  height: 40,
  minHeight: 40,
  minWidth: 77,
  py: "8px",
  px: "16px",
  gap: "4px",
  bgcolor: "#FFFFFF",
  color: C.primary,
  borderRadius: "4px",
  border: `1px solid ${C.primary}`,
  boxShadow: DRAWER_BTN_SHADOW,
  "&:hover": {
    bgcolor: "#F0F6FF",
    border: `1px solid ${C.primary}`,
    boxShadow: DRAWER_BTN_SHADOW,
  },
};

const questionsSubtextSx = {
  fontFamily: "Lato, sans-serif",
  fontWeight: 400,
  fontSize: 14,
  lineHeight: "24px",
  letterSpacing: 0,
  color: "#555555",
};

const emptyQuestionsTitleSx = {
  fontFamily: "Lato, sans-serif",
  fontWeight: 700,
  fontSize: 16,
  lineHeight: "24px",
  letterSpacing: "0.15px",
  textAlign: "center",
  color: "#383838",
  minWidth: 201,
  width: "auto",
  minHeight: 24,
  whiteSpace: "nowrap",
  mx: "auto",
  mb: 2,
};

const REMOVE_QUESTION_COLOR = "#DE350B";

const removeQuestionBtnSx = {
  textTransform: "none",
  fontFamily: "Lato, sans-serif",
  fontWeight: 700,
  fontSize: 14,
  lineHeight: "24px",
  letterSpacing: 0,
  color: REMOVE_QUESTION_COLOR,
  minWidth: 0,
  height: 32,
  minHeight: 32,
  py: "4px",
  px: 0,
  m: 0,
  mt: 0.5,
  ml: 0,
  bgcolor: "transparent",
  borderRadius: "4px",
  border: "none",
  boxSizing: "border-box",
  boxShadow: "none",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "flex-start",
  "&:hover": {
    bgcolor: "transparent",
    color: REMOVE_QUESTION_COLOR,
    textDecoration: "underline",
    boxShadow: "none",
  },
  "&:focus": {
    bgcolor: "transparent",
    color: REMOVE_QUESTION_COLOR,
    textDecoration: "none",
    boxShadow: "none",
    outline: `2px solid ${REMOVE_QUESTION_COLOR}`,
    outlineOffset: 0,
  },
  "&:focus-visible": {
    bgcolor: "transparent",
    color: REMOVE_QUESTION_COLOR,
    textDecoration: "none",
    boxShadow: "none",
    outline: `2px solid ${REMOVE_QUESTION_COLOR}`,
    outlineOffset: 0,
  },
  "&:active": {
    bgcolor: "#DE350B14",
    color: REMOVE_QUESTION_COLOR,
    boxShadow: "none",
    outline: "none",
  },
};

const addNewQuestionBtnSx = {
  textTransform: "none",
  fontWeight: 600,
  color: C.primary,
  fontSize: 13,
  mt: 1,
  "&:hover": {
    bgcolor: "transparent",
    color: C.primary,
    textDecoration: "underline",
  },
  "&:focus": {
    bgcolor: "transparent",
    color: C.primary,
    textDecoration: "none",
    outline: `2px solid ${C.primary}`,
    outlineOffset: "2px",
  },
  "&:focus-visible": {
    bgcolor: "transparent",
    color: C.primary,
    textDecoration: "none",
    outline: `2px solid ${C.primary}`,
    outlineOffset: "2px",
  },
};

const emptyAddQuestionsBtnSx = {
  textTransform: "none",
  fontFamily: "Lato, sans-serif",
  fontWeight: 700,
  fontSize: 15,
  lineHeight: "24px",
  letterSpacing: 0,
  width: 132,
  minWidth: 132,
  height: 40,
  minHeight: 40,
  py: "8px",
  px: "16px",
  gap: "4px",
  bgcolor: "#FFFFFF",
  color: C.primary,
  borderRadius: "4px",
  border: `1px solid ${C.primary}`,
  boxShadow: DRAWER_BTN_SHADOW,
  whiteSpace: "nowrap",
  "&:hover": {
    bgcolor: "#F0F6FF",
    border: `1px solid ${C.primary}`,
    boxShadow: DRAWER_BTN_SHADOW,
  },
};

const fieldSx = {
  "& .MuiInputBase-input::placeholder": {
    color: C.muted,
    opacity: 1,
  },
  "& .MuiFormHelperText-root": {
    marginLeft: 0,
    marginRight: 0,
    color: C.muted,
    "&.Mui-disabled": {
      color: C.muted,
    },
  },
  "& .MuiOutlinedInput-root": {
    borderRadius: "4px",
    fontSize: 14,
    "& fieldset": { borderColor: C.border },
    "&:hover fieldset": { borderColor: C.border },
    "&.Mui-focused fieldset": { borderColor: C.primary, borderWidth: "1px" },
  },
};

export default function SectionEditorDrawer({
  open,
  mode,
  readOnly = false,
  draft,
  originalName,
  onClose,
  onSave,
}) {
  const [local, setLocal] = useState(draft);
  const [thresholdInput, setThresholdInput] = useState("");
  const [nameEditable, setNameEditable] = useState(mode === "add");
  const [scoreEditable, setScoreEditable] = useState(mode === "add");
  const [nameAtEditStart, setNameAtEditStart] = useState("");
  const [thresholdAtEditStart, setThresholdAtEditStart] = useState(85);
  const [pendingThreshold, setPendingThreshold] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null);

  const syncThresholdInput = (threshold) => {
    setThresholdInput(String(normalizeThresholdPercent(threshold ?? 85)));
  };

  useEffect(() => {
    if (open && draft) {
      setLocal(draft);
      syncThresholdInput(draft.threshold);
      setNameEditable(mode === "add");
      setScoreEditable(mode === "add");
    }
  }, [open, draft, mode]);

  if (!local) return null;

  const update = (patch) => setLocal((prev) => ({ ...prev, ...patch }));

  const parseThresholdInput = () => {
    const parsed = thresholdInput.trim() === "" ? local.threshold : thresholdInput;
    return normalizeThresholdPercent(parsed, local.threshold ?? 85);
  };

  const commitThresholdInput = () => {
    const v = parseThresholdInput();
    setThresholdInput(String(v));
    update({ threshold: v });
    return v;
  };

  const beginNameEdit = () => {
    setNameAtEditStart(local.name || "");
    setNameEditable(true);
  };

  const beginScoreEdit = () => {
    const current = normalizeThresholdPercent(local.threshold ?? 85);
    setThresholdAtEditStart(current);
    syncThresholdInput(current);
    setScoreEditable(true);
  };

  const lockNameField = () => {
    setNameEditable(false);
    setNameAtEditStart(local.name || "");
  };

  const lockScoreField = (threshold) => {
    const v = normalizeThresholdPercent(threshold ?? local.threshold ?? 85);
    setThresholdInput(String(v));
    update({ threshold: v });
    setScoreEditable(false);
    setThresholdAtEditStart(v);
  };

  const handleNameKeyDown = (e) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    if (mode !== "edit" || !nameEditable) return;

    const trimmed = (local.name || "").trim();
    if (!trimmed) return;

    const unchanged = trimmed === (nameAtEditStart || "").trim();
    if (unchanged) {
      lockNameField();
      return;
    }

    setConfirmDialog("name");
  };

  const handleThresholdKeyDown = (e) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    if (mode !== "edit" || !scoreEditable) return;

    const v = parseThresholdInput();
    setThresholdInput(String(v));

    const unchanged = v === normalizeThresholdPercent(thresholdAtEditStart);
    if (unchanged) {
      lockScoreField(v);
      return;
    }

    setPendingThreshold(v);
    setConfirmDialog("score");
  };

  const handleConfirmNameUpdate = () => {
    lockNameField();
    setConfirmDialog(null);
  };

  const handleCancelNameUpdate = () => {
    update({ name: nameAtEditStart });
    lockNameField();
    setConfirmDialog(null);
  };

  const handleConfirmScoreUpdate = () => {
    if (pendingThreshold !== null) {
      lockScoreField(pendingThreshold);
    }
    setPendingThreshold(null);
    setConfirmDialog(null);
  };

  const handleCancelScoreUpdate = () => {
    const reverted = normalizeThresholdPercent(thresholdAtEditStart);
    setThresholdInput(String(reverted));
    update({ threshold: reverted });
    setScoreEditable(false);
    setPendingThreshold(null);
    setConfirmDialog(null);
  };

  const syncSectionRequired = (items) => sectionRequiredFromItems(items);

  const addQuestion = () => {
    const items = [...(local.items || [])];
    const id = uniqueItemId(local.id, new Set(items.map((x) => x.id)));
    items.push({ id, text: "", required: true });
    update({ items, required: syncSectionRequired(items) });
  };

  const updateQuestion = (index, patch) => {
    const items = local.items.map((it, i) => (i === index ? { ...it, ...patch } : it));
    update({ items, required: syncSectionRequired(items) });
  };

  const removeQuestion = (index) => {
    const items = local.items.filter((_, i) => i !== index);
    update({ items, required: syncSectionRequired(items) });
  };

  const handleSave = () => {
    if (!local.name?.trim()) return;
    if (!(local.items || []).length) return;
    if ((local.items || []).some((it) => !(it.text || "").trim())) return;
    const threshold = normalizeThresholdPercent(
      thresholdInput.trim() === "" ? local.threshold : thresholdInput,
      local.threshold ?? 85
    );
    onSave({ ...local, threshold, required: syncSectionRequired(local.items || []) });
  };

  const questionCount = local.items?.length || 0;
  const canSave =
    Boolean(local.name?.trim()) &&
    questionCount > 0 &&
    (local.items || []).every((it) => (it.text || "").trim());

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: C.drawerWidth },
          maxWidth: "100vw",
          boxShadow: "-4px 0 24px rgba(0,0,0,0.12)",
        },
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%", bgcolor: "#fff" }}>
        <Box sx={{ px: 2.5, pt: 2.5, pb: 2, borderBottom: `1px solid ${C.border}` }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <Typography sx={{ fontSize: 18, fontWeight: 700, color: C.text }}>
              {readOnly ? "View Section" : mode === "add" ? "Add New Section" : "Edit Section"}
            </Typography>
            <IconButton size="small" onClick={onClose} aria-label="Close">
              <CloseIcon />
            </IconButton>
          </Box>
          <Typography sx={{ fontSize: 13, color: C.muted, mt: 1, lineHeight: 1.5 }}>
            {readOnly
              ? "This section is fixed by ValidAIte to capture the application's functional requirements. It can't be edited or removed — you'll answer these questions in the chat."
              : "Create a custom section to capture information that isn't covered by the standard assessment."}
          </Typography>
        </Box>

        <Box sx={{ flex: 1, overflow: "auto", px: 2.5, py: 2 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.75 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: C.text }}>
              Section Name
              <RequiredMark />
            </Typography>
            {!readOnly && mode === "edit" && !nameEditable && (
              <Link
                component="button"
                type="button"
                onClick={beginNameEdit}
                sx={{ fontSize: 13, fontWeight: 600, color: C.primary, textDecoration: "none" }}
              >
                Edit
              </Link>
            )}
          </Box>
          <TextField
            fullWidth
            size="small"
            value={local.name}
            onChange={(e) => update({ name: e.target.value })}
            onKeyDown={handleNameKeyDown}
            disabled={readOnly || (mode === "edit" && !nameEditable)}
            placeholder="e.g. Identity & Ownership"
            sx={{ ...fieldSx, mb: 2 }}
          />

          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.75 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: C.text }}>
              Confidence threshold (%)
              <RequiredMark />
            </Typography>
            {!readOnly && mode === "edit" && !scoreEditable && (
              <Link
                component="button"
                type="button"
                onClick={beginScoreEdit}
                sx={{ fontSize: 13, fontWeight: 600, color: C.primary, textDecoration: "none" }}
              >
                Edit
              </Link>
            )}
          </Box>
          <TextField
            fullWidth
            size="small"
            type="text"
            inputMode="numeric"
            inputProps={{ maxLength: 3, "aria-label": "Confidence threshold percent" }}
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
            }}
            value={thresholdInput}
            onChange={(e) => {
              const digits = e.target.value.replace(/\D/g, "");
              if (digits === "") {
                setThresholdInput("");
                return;
              }
              const n = parseInt(digits, 10);
              if (Number.isNaN(n)) return;
              setThresholdInput(String(Math.min(100, n)));
            }}
            onKeyDown={handleThresholdKeyDown}
            onBlur={() => {
              if (mode === "edit" && scoreEditable) return;
              commitThresholdInput();
            }}
            disabled={readOnly || (mode === "edit" && !scoreEditable)}
            helperText="10–100% — section completes when intake score reaches this level"
            sx={{ ...fieldSx, mb: 2.5 }}
          />

          <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "baseline", gap: 1, mb: 2 }}>
            <Typography component="span" sx={questionsHeadingSx}>
              {questionCount > 0 ? `Questions (${questionCount})` : "Questions"}
            </Typography>
            <Typography component="span" sx={questionsSubtextSx}>
              Add the questions the chatbot should ask to gather information for this section.
            </Typography>
          </Box>

          {questionCount === 0 ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <FolderEmptyIcon
                style={{
                  width: 180,
                  height: 180,
                  display: "block",
                  margin: "0 auto 8px",
                }}
                aria-hidden
              />
              <Typography sx={emptyQuestionsTitleSx}>No Questions Created</Typography>
              <Button variant="outlined" onClick={addQuestion} sx={emptyAddQuestionsBtnSx}>
                Add Questions
              </Button>
            </Box>
          ) : (
            <>
              {local.items.map((item, idx) => {
                const gate0Linked = isGate0MappedQuestion(item);
                const gate0Active = gate0Linked && shouldKeepGate0Mapping(item, local.id);
                return (
                <Box key={item.id || idx} sx={{ mb: 2 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.75 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                      <QuestionLabel number={idx + 1} required={item.required} />
                      {gate0Linked && (
                        <Typography
                          component="span"
                          sx={{
                            fontSize: 11,
                            fontWeight: 700,
                            color: gate0Active ? C.primary : C.muted,
                            bgcolor: gate0Active ? "#E8F1FE" : "#F5F5F5",
                            px: 1,
                            py: 0.25,
                            borderRadius: "4px",
                            lineHeight: 1.6,
                          }}
                        >
                          App Identity
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  <TextField
                    fullWidth
                    size="small"
                    multiline
                    minRows={2}
                    value={item.text}
                    onChange={(e) => updateQuestion(idx, { text: e.target.value })}
                    disabled={readOnly}
                    placeholder="e.g., What is the criticality of your AI application?"
                    helperText={
                      gate0Linked
                        ? gate0Active
                          ? `Linked to App Identity (${gate0FieldLabel(item.gate0_field)}). Answers auto-fill from App Identity while this text stays unchanged.`
                          : `App Identity link removed — change saved will clear the auto-filled answer for this question.`
                        : undefined
                    }
                    sx={fieldSx}
                  />
                  {!gate0Linked && !readOnly && (
                    <Button
                      variant="text"
                      disableRipple
                      onClick={() => removeQuestion(idx)}
                      sx={removeQuestionBtnSx}
                    >
                      Remove Question
                    </Button>
                  )}
                </Box>
                );
              })}
              {!readOnly && (
                <Button
                  startIcon={<AddIcon style={{ width: 14, height: 14 }} />}
                  onClick={addQuestion}
                  sx={addNewQuestionBtnSx}
                >
                  Add New Question
                </Button>
              )}
            </>
          )}
        </Box>

        <Box sx={drawerFooterSx}>
          <Typography sx={drawerFooterNoteSx}>
            {readOnly
              ? "Fixed by ValidAIte — answered in the chat, not here."
              : "Changes recalculate section score immediately."}
          </Typography>           
          <Box sx={{ display: "flex", gap: "16px", flexShrink: 0 }}>
            {readOnly ? (
              <Button variant="contained" onClick={onClose} sx={{
                textTransform: "none",
                fontWeight: 600,
                bgcolor: C.primary,
                borderRadius: "4px",
                minWidth: 110,
                "&:hover": { bgcolor: "#0046B8" },
              }}>
                Close
              </Button>
            ) : (
              <>
                <Button variant="outlined" onClick={onClose} sx={drawerCancelBtnSx}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSave}
                  disabled={!canSave}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    bgcolor: C.primary,
                    borderRadius: "4px",
                    minWidth: 110,
                    "&:hover": { bgcolor: "#0046B8" },
                  }}
                >
                  Save & Close
                </Button>
              </>
            )}
          </Box>
        </Box>
      </Box>

      <ConfirmFieldUpdateDialog
        open={confirmDialog === "name"}
        title={CONFIRM_NAME_COPY.title}
        description={CONFIRM_NAME_COPY.description}
        confirmLabel="Continue"
        onCancel={handleCancelNameUpdate}
        onConfirm={handleConfirmNameUpdate}
      />

      <ConfirmFieldUpdateDialog
        open={confirmDialog === "score"}
        title={CONFIRM_SCORE_COPY.title}
        description={CONFIRM_SCORE_COPY.description}
        confirmLabel="Continue"
        onCancel={handleCancelScoreUpdate}
        onConfirm={handleConfirmScoreUpdate}
      />
    </Drawer>
  );
}

export function needsSectionNameConfirm(mode, originalName, newName) {
  return mode === "edit" && originalName && newName.trim() !== originalName.trim();
}
